'use client';

import Link from 'next/link';
import { FormEvent, JSX, useState, useRef, useEffect } from 'react';
import { twMerge } from 'tailwind-merge';

import { Button, Text } from '@videoblocks/design-system';

import CircleSpinner from '../../_components/CircleSpinner';
import ErrorBox from '../../_components/ErrorBox';
import { updateUserYoutubeChannels } from '../actions';

interface YouTubeChannelsFormProps {
    userId: number;
    initialChannels: string[];
}

export const MAX_EDITABLE_CHANNELS = 2;

function normalizeChannels(channels: string[]): string[] {
    const normalized = [...channels];
    while (normalized.length < MAX_EDITABLE_CHANNELS) {
        normalized.push('');
    }
    return normalized;
}

export default function YouTubeChannelsForm({ userId, initialChannels }: YouTubeChannelsFormProps): JSX.Element {
    const [isEditable, setIsEditable] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [channels, setChannels] = useState<string[]>(() => normalizeChannels(initialChannels));
    const [formValues, setFormValues] = useState<string[]>(() => normalizeChannels(initialChannels));
    const formRef = useRef<HTMLFormElement>(null);

    const canEdit = initialChannels.length <= MAX_EDITABLE_CHANNELS;

    useEffect(() => {
        const normalized = normalizeChannels(initialChannels);
        setChannels(normalized);
        setFormValues(normalized);
    }, [initialChannels]);

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setIsLoading(true);
        setError('');

        try {
            const channel1 = formValues[0] || '';
            const channel2 = formValues[1] || '';

            const result = await updateUserYoutubeChannels(userId, [channel1, channel2]);

            if (result.success) {
                setChannels(normalizeChannels([channel1, channel2]));
                setIsEditable(false);
            } else {
                setError(result.error || 'Failed to update YouTube channels. Please try again.');
            }
        } catch (err) {
            setError('Failed to update YouTube channels. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const openEditing = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (canEdit) {
            setFormValues([...channels]);
            setIsEditable(true);
        }
    };

    const cancelEdit = () => {
        setIsEditable(false);
        setError('');
        setFormValues([...channels]);
    };

    return (
        <div className="flex flex-col space-y-4 mt-18 lg:w-3/4 2xl:w-2/3">
            <Text fontSize="2xl" fontWeight="semibold" asChild>
                <h2>My YouTube Channels</h2>
            </Text>
            <Text fontWeight="regular">
                Add your YouTube channels here to avoid receiving copyright claims. By providing us with your YouTube
                channel ID, we are able to remove the majority of claims automatically in less than 30 minutes.
            </Text>
            <Text>
                <a
                    href="https://support.google.com/youtube/answer/3250431?hl=en"
                    className="text-blue-600 hover:underline"
                    target="_blank"
                >
                    Where do I find my YouTube channel ID?
                </a>
            </Text>

            <form ref={formRef} onSubmit={canEdit && isEditable ? handleSubmit : openEditing} className="flex flex-col">
                <ErrorBox message={error} onDismiss={() => setError('')} />

                {channels.map((channel, index) => (
                    <div key={index} className="flex flex-row items-center py-4 border-b-1 border-gray-300">
                        <label htmlFor={`youtubeChannel${index + 1}`} className="w-full">
                            YouTube Channel ID {index + 1}
                        </label>
                        <input
                            className={twMerge(
                                'w-full p-2 rounded-sm border border-solid border-white',
                                isEditable && canEdit && 'border-gray-300',
                            )}
                            id={`youtubeChannel${index + 1}`}
                            name={`youtubeChannel${index + 1}`}
                            placeholder={channel || 'ex: WJL0W9fg9queUHP471uE0xg'}
                            value={isEditable ? formValues[index] || '' : channel}
                            onChange={(e) => {
                                if (isEditable) {
                                    const newFormValues = [...formValues];
                                    newFormValues[index] = e.target.value;
                                    setFormValues(newFormValues);
                                }
                            }}
                            type="text"
                            disabled={!isEditable || !canEdit}
                        />
                    </div>
                ))}

                {canEdit && (
                    <div className="flex flex-row items-center py-4 mt-4">
                        <Button
                            role="submit"
                            type="submit"
                            variant="outlined-dark"
                            className="rounded-full"
                            buttonClassName="rounded-full"
                            disabled={isLoading}
                        >
                            {isEditable ? 'Save Changes' : 'Edit YouTube Channels'}
                        </Button>
                        {isEditable && (
                            <>
                                <button
                                    onClick={() => cancelEdit()}
                                    role="button"
                                    type="reset"
                                    className="w-36 border-none bg-none text-red-400 font-semibold hover:underline"
                                >
                                    Cancel
                                </button>
                                {isLoading && <CircleSpinner />}
                            </>
                        )}
                    </div>
                )}
            </form>

            <div className="space-y-6">
                <Text fontWeight="regular">
                    Contact{' '}
                    <button
                        type="button"
                        className="text-blue-600 hover:underline font-semibold"
                        onClick={() => {
                            if (typeof window !== 'undefined' && window.Intercom) {
                                window.Intercom('show');
                            }
                        }}
                    >
                        support
                    </button>{' '}
                    to add additional channels.
                </Text>
                <Text fontWeight="semibold">
                    <Link href="/member/youtube" className="text-blue-600 hover:underline">
                        Dispute a Copyright Claim
                    </Link>
                </Text>
            </div>
        </div>
    );
}
